import { useEffect, useState, useCallback, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const closeRef = useRef(null);

  const handleKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onPrev();
      else if (e.key === "ArrowRight") onNext();
    },
    [onClose, onPrev, onNext]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [handleKey]);

  if (index == null || !photos[index]) return null;
  const photo = photos[index];

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (touchStart == null || touchEnd == null) return;
    const dist = touchStart - touchEnd;
    const threshold = 50;
    if (dist > threshold) onNext();
    else if (dist < -threshold) onPrev();
  };

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[60] lb-backdrop flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Vista ampliada de imagen"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <button
        ref={closeRef}
        data-testid="lightbox-close-btn"
        onClick={onClose}
        aria-label="Cerrar"
        className="absolute top-4 right-4 md:top-6 md:right-6 z-10 h-11 w-11 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center transition"
      >
        <X className="h-5 w-5" />
      </button>

      <button
        data-testid="lightbox-prev-btn"
        onClick={onPrev}
        aria-label="Anterior"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20 items-center justify-center transition"
      >
        <ChevronLeft className="h-6 w-6" />
      </button>
      <button
        data-testid="lightbox-next-btn"
        onClick={onNext}
        aria-label="Siguiente"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-white/10 text-white hover:bg-white/20 items-center justify-center transition"
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      <div className="relative max-w-[92vw] max-h-[88vh] w-full h-full flex flex-col items-center justify-center px-4 md:px-16">
        <img
          key={photo.id}
          src={photo.src}
          alt={photo.alt}
          className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl"
        />
        <div className="mt-4 flex items-center justify-between w-full max-w-3xl text-white/80 text-sm">
          <span className="uppercase tracking-[0.18em] text-[11px] font-semibold text-[var(--fb-accent)]">
            {photo.category}
          </span>
          <span className="text-white/55 text-xs">
            {index + 1} / {photos.length}
          </span>
        </div>
        <p className="mt-1 text-white/80 text-sm md:text-base text-center max-w-3xl">
          {photo.alt}
        </p>
      </div>
    </div>
  );
}
